import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Box} from '@dropbox/dig-foundations';
import {SpeechBubblesMini} from '@dropbox/dig-illustrations';
import {activeCommentAtom, focusedThreadAtom} from 'atoms/comment';
import {snackbarAtom} from 'atoms/snackbar';
import {DocumentThread} from 'client';
import {CommentThreadSections} from 'components/DSYS/CommentThreadSections';
import {useThreadsWithoutContext} from 'components/DSYS/editor/plugins/comments/useThreads';
import {EmptyState} from 'components/DSYS/EmptyState';
import {Title} from 'components/DSYS/Title';
import {useCommentActions} from 'hooks/useCommentActions';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {findAttachedCommentElement} from './commentsHelpers';
import {PlanningCommentThread} from './PlanningCommentThread';
import {isThreadAttachedToActiveIds, useActiveThreadIds, useFilteredThreads} from './utils';

export const PlanningCommentsDrawer = ({
  documentId,
  editorContainerEl,
  handleCloseDrawer,
}: {
  documentId: string;
  editorContainerEl?: HTMLElement | null;
  handleCloseDrawer: () => void;
}) => {
  const {threads, isLoading} = useThreadsWithoutContext(documentId);
  const threadRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const focusedThread = useAtomValue(focusedThreadAtom);
  const activeComment = useAtomValue(activeCommentAtom); // Needed for toggling inline comment
  const {openComment} = useCommentActions();
  const setSnackbarMessage = useSetAtom(snackbarAtom);

  // Filter threads to only show those with at least one active (non-deleted) comment
  const activeThreadIds = useActiveThreadIds(
    editorContainerEl === undefined ? undefined : {root: editorContainerEl}
  );
  const filteredThreads = useFilteredThreads(threads);

  const {documentThreads, orphanedThreads} = useMemo(() => {
    const threadMap = new Map(filteredThreads.map((thread) => [thread.id, thread]));
    const activeSet = new Set(activeThreadIds || []);
    const activeDocumentThreads = (activeThreadIds || [])
      .map((id) => threadMap.get(id))
      .filter((thread): thread is DocumentThread => thread !== undefined);
    const attachedThreadIds = new Set(activeDocumentThreads.map((thread) => thread.id));
    const additionalAttachedThreads = filteredThreads.filter(
      (thread) =>
        !attachedThreadIds.has(thread.id) && isThreadAttachedToActiveIds(thread, activeSet)
    );
    const allDocumentThreads = [...activeDocumentThreads, ...additionalAttachedThreads];
    const allDocumentThreadIds = new Set(allDocumentThreads.map((thread) => thread.id));
    const unattachedThreads = filteredThreads.filter(
      (thread) => !allDocumentThreadIds.has(thread.id)
    );

    return {
      documentThreads: allDocumentThreads,
      orphanedThreads: unattachedThreads,
    };
  }, [activeThreadIds, filteredThreads]);

  const resolvedThreads = useMemo(
    () =>
      (threads ?? []).filter(
        (thread) => thread.is_resolved && thread.comments?.some((comment) => !comment.is_deleted)
      ),
    [threads]
  );
  const activeSections = [
    {
      id: 'comments-in-document',
      threads: documentThreads,
    },
    {
      id: 'comments-not-in-document',
      title: `${t('comments_not_in_document')} (${t('comments', {
        count: orphanedThreads.length,
      })})`,
      threads: orphanedThreads,
    },
  ];
  const threadSections = [
    ...activeSections,
    {
      id: 'resolved-comments',
      title: `${t('resolved_comments')} (${t('comments', {count: resolvedThreads.length})})`,
      threads: resolvedThreads,
      muted: true,
    },
  ];
  const isEmpty = threadSections.every((section) => section.threads.length === 0);

  // Record if the user has scrolled recently
  const userScrolledRecentlyRef = useRef(false);
  useEffect(() => {
    let timeout: number | null = null;

    const mark = () => {
      userScrolledRecentlyRef.current = true;
      if (timeout != null) window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        userScrolledRecentlyRef.current = false;
        timeout = null;
      }, 300);
    };

    window.addEventListener('wheel', mark, {passive: true});
    window.addEventListener('touchmove', mark, {passive: true});

    return () => {
      window.removeEventListener('wheel', mark);
      window.removeEventListener('touchmove', mark);
      if (timeout != null) window.clearTimeout(timeout);
    };
  }, []);

  // Scroll active comment in drawer into view
  useEffect(() => {
    if (!activeComment) {
      // Only close if the user just scrolled
      if (userScrolledRecentlyRef.current) {
        handleCloseDrawer();
      }
      return;
    }

    const threadElement = document.querySelector(
      `[data-planning-drawer-thread-id="${activeComment.threadId}"]`
    );
    if (threadElement) {
      threadElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, [activeComment, handleCloseDrawer]);

  const handleThreadSelect = useCallback(
    (threadId: string) => {
      if (!findAttachedCommentElement(threadId, editorContainerEl ?? document)) {
        setSnackbarMessage({text: t('comment_not_attached')});
      }
      if (!openComment({threadId, source: 'drawer'})) return;

      // Wait for everything to settle before closing drawer
      requestAnimationFrame(() => handleCloseDrawer());
    },
    [editorContainerEl, openComment, handleCloseDrawer, setSnackbarMessage]
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Spinner size="medium" />
      </Box>
    );
  }

  if (isEmpty) {
    return <PlanningCommentsDrawerEmptyState />;
  }

  const getCommentThreadProps = (thread: DocumentThread) => {
    return {
      ref: (el: HTMLDivElement | null) => {
        threadRefs.current[thread.id] = el;
      },
      thread,
      isSelected: focusedThread === thread.id,
      onThreadSelect: () => handleThreadSelect(thread.id),
    };
  };

  return (
    <CommentThreadSections
      sections={threadSections.map((section) => ({
        ...section,
        renderThread: (thread: DocumentThread) => (
          <PlanningCommentThread
            key={`planning-comments-drawer-thread__${thread.id}`}
            {...getCommentThreadProps(thread)}
          />
        ),
      }))}
    />
  );
};

export const PlanningCommentsDrawerHeader = ({documentId}: {documentId: string}) => {
  const {threads, isLoading} = useThreadsWithoutContext(documentId);

  const filteredThreads = useFilteredThreads(threads);
  const resolvedThreads =
    threads?.filter(
      (thread) => thread.is_resolved && thread.comments?.some((comment) => !comment.is_deleted)
    ) ?? [];

  const commentCount = useMemo(() => {
    if (isLoading) return 0;

    return filteredThreads.length + resolvedThreads.length;
  }, [isLoading, resolvedThreads.length, filteredThreads.length]);

  return isLoading ? (
    <Box paddingTop="2" style={{width: 120, height: 24}}>
      <Skeleton.Text size="large" width={120} />
    </Box>
  ) : (
    <Title size={18}>{t('comments', {count: commentCount})}</Title>
  );
};

const PlanningCommentsDrawerEmptyState = () => (
  <EmptyState
    hideBorder
    title={t('no_comments')}
    body={t('comments_will_appear_here')}
    image={<SpeechBubblesMini width={128} height={128} altText="Speech Bubbles" />}
  />
);
